import { escapeCsvCell, stringifyCell } from "../csv/csv-cell";
import type { AnalyticsRenderInput } from "./analytics-render-input";

/**
 * The UTF-8 BOM. Excel needs it at the head of a CSV to auto-detect UTF-8 —
 * without it, accented / CJK / Arabic values open as mojibake on Windows.
 *
 * Exported so a caller can decide where it goes: the server writes it into the
 * bytes it uploads, and the browser hands it to a `Blob` alongside the text.
 */
export const UTF8_BOM = "\uFEFF";

/**
 * Render an analytics query result to CSV text. Column order follows `fields`;
 * each row cell is looked up by field name. The whole result set is already
 * materialised in memory (the executor caps at 1000 rows), so this is a simple
 * synchronous build rather than a stream.
 *
 * Every cell runs through the shared formula-injection-safe RFC-4180 escaper,
 * and lines are CRLF-terminated — the form Excel expects on every platform.
 *
 * Returns text WITHOUT the BOM: the byte-order mark is an encoding concern of
 * whatever container the text lands in, and prepending it here would mean a
 * caller that wants the raw text has to strip it back off. `UTF8_BOM` is
 * exported beside this for the callers that do need it (both of them, today).
 */
export function analyticsResultToCsvText(input: AnalyticsRenderInput): string {
  const columns = input.fields.map((f) => f.name);

  const lines: string[] = [];
  lines.push(columns.map((name) => escapeCsvCell(name)).join(","));

  for (const row of input.rows) {
    lines.push(columns.map((name) => escapeCsvCell(stringifyCell(row[name]))).join(","));
  }

  return `${lines.join("\r\n")}\r\n`;
}
